<template>
  <div class="AdminPanel px-8" v-if="!isLoading">
    <AdminTopBar
      page="Members"
      :items="adminStore.appMembers"
      :sortingListItems="[
        { sortingKey: 'firstName', text: $t('auth.becomeMember.form.firstName') },
        { sortingKey: 'lastName', text: $t('auth.becomeMember.form.lastName') },
        { sortingKey: 'email', text: $t('auth.becomeMember.form.email') },
      ]"
      searchKey="email"
      @updateSortingKey="sortingUsersSelectedMethod = $event"
      @update-search-query="searchQuery = $event"
    >
      <template #right-buttons>
        <button
          v-if="userStore.userPeutCreerUtilisateurs()"
          class="rounded-full px-0 w-[38px] md:w-auto bg-main hover:bg-main-hover text-white font-medium md:rounded-md transition-colors cursor-pointer md:px-5 h-[38px] center-blocs"
          @click="createUser"
        >
          <span class="hidden md:block">{{ $t('admin.add') }}</span>
          <Icon icon="material-symbols:add-rounded" class="text-white block md:hidden size-4" />
        </button>
      </template>
    </AdminTopBar>
    <AdminTable
      :items="filteredItems"
      :tableKeys="['userNom', 'userPrenom', 'userEmail', 'profil']"
      :headers="[
        $t('auth.becomeMember.form.lastName'),
        $t('auth.becomeMember.form.firstName'),
        $t('auth.becomeMember.form.email'),
        $t('admin.users.profile'),
        $t('auth.becomeMember.form.actions'),
      ]"
      :columnWidths="['17%', '17%', '26%', '26%', '14%']"
      :plainText="true"
    >
      <template #cell-profil="{ item }">
        <div v-if="(item as User).profil" class="flex flex-col min-w-0 leading-tight">
          <span class="truncate text-sm text-gray-800">{{ libelleProfil(item as User) }}</span>
          <span v-if="(item as User).commune" class="truncate text-xs text-gray-500">
            {{ (item as User).commune?.cmnTitre }}
          </span>
        </div>
      </template>
      <template #editContentCell="{ item }">
        <div class="flex items-center gap-1">
          <span
            v-if="!(item as User).isValidated"
            class="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
            :title="$t('auth.editForm.waitingValidation')"
          >
            <Icon icon="mdi:account-clock-outline" class="w-4 h-4" />
          </span>

          <button
            v-if="userStore.userPeutModifierUtilisateurs()"
            @click="editUser(item as User)"
            class="p-1.5 rounded-full center-blocs hover:bg-gray-100 hover:text-main transition-colors cursor-pointer"
            :title="$t('forms.edit')"
          >
            <Icon icon="mdi:pencil-outline" />
          </button>

          <button
            v-if="
              userStore.userPeutSupprimerUtilisateurs() &&
              (item as User).id !== userStore.currentUser?.id
            "
            @click="((isAreYouSurePopupShown = true), (userToDelete = item as Partial<User>))"
            class="p-1.5 rounded-full center-blocs hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
            :title="$t('admin.delete')"
          >
            <Icon icon="material-symbols-light:delete" size="24" />
          </button>
        </div>
      </template>
    </AdminTable>

    <!-- Une seule popup, hors des lignes : une par ligne les ouvrait toutes à la fois, et le clic sur
         « Supprimer » fermait les autres (clic extérieur), ce qui vidait userToDelete avant la confirmation. -->
    <AreYouSurePopup
      :isOpen="isAreYouSurePopupShown"
      :title="$t('admin.users.deleteTitle')"
      :message="$t('admin.users.deleteWarning')"
      :itemApiDetails="userToDelete?.userNom"
      @close="((isAreYouSurePopupShown = false), (userToDelete = null))"
      @confirm="deleteUser()"
    />
  </div>
  <div v-else class="w-full h-full">
    <SpinnerLoader></SpinnerLoader>
  </div>
</template>
<script setup lang="ts">
import AdminTable from '@/components/admin/AdminTable.vue'
import AdminTopBar from '@/components/admin/AdminTopBar.vue'
import AreYouSurePopup from '@/components/global/AreYouSurePopup.vue'
import type { User } from '@/models/interfaces/auth/User'
import { useAdminStore } from '@/stores/adminStore.ts'
import { useUserStore } from '@/stores/userStore'
import Icon from '@/components/ui/Icon.vue'
import { computed, onBeforeMount, ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'
const adminStore = useAdminStore()
const userStore = useUserStore()
const { t, te } = useI18n()

const libelleProfil = (user: User) => {
  const cle = `auth.profils.${user.profil?.profilRefCode}`
  return te(cle) ? t(cle) : (user.profil?.profilTitre ?? '')
}
const isAreYouSurePopupShown = ref(false)
//const isDeleting = ref(false)
const isLoading = ref(false)
onBeforeMount(async () => {
  isLoading.value = true
  await adminStore.getMembers()
  isLoading.value = false
})

function createUser() {
  adminStore.userEdition.active = true
  adminStore.setUserEditionMode(null)
}

function editUser(user: User) {
  adminStore.setUserEditionMode(user)
}

const userToDelete: Ref<Partial<User> | null> = ref(null)
const deleteUser = async () => {
  const user = userToDelete.value
  if (!user?.id) return
  isLoading.value = true
  await adminStore.deleteUser(user)
  isLoading.value = false
  userToDelete.value = null
  isAreYouSurePopupShown.value = false
}

const sortingUsersSelectedMethod = ref('lastName')
const sortedUsers = computed(() => {
  if (sortingUsersSelectedMethod.value === 'firstName') {
    return adminStore.appMembers.slice().sort((a: User, b: User) => {
      return a.userPrenom.localeCompare(b.userPrenom)
    })
  }
  if (sortingUsersSelectedMethod.value === 'lastName') {
    return adminStore.appMembers.slice().sort((a: User, b: User) => {
      return a.userNom.localeCompare(b.userNom)
    })
  }
  if (sortingUsersSelectedMethod.value === 'email') {
    return adminStore.appMembers.slice().sort((a: User, b: User) => {
      return a.userEmail.localeCompare(b.userEmail)
    })
  }
  return adminStore.appMembers
})

const searchQuery = ref('')
const filteredItems = computed(() => {
  if (!searchQuery.value) {
    return sortedUsers.value
  }
  return sortedUsers.value.filter((item: User) => {
    return (
      item.userPrenom.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      item.userNom.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      item.profil?.profilTitre.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      //item.organisation?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      item.userEmail.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  })
})
</script>

<style scoped></style>
